
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, Users, Edit, Trash2, Plus } from 'lucide-react';
import { TimeSlot, DayOfWeek } from '@/models/Schedule';
import { useTranslation } from 'react-i18next';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';

interface TimeSlotGridProps {
  timeSlots: TimeSlot[];
  onEditTimeSlot: (timeSlot: TimeSlot) => void;
  onDeleteTimeSlot: (timeSlot: TimeSlot) => void;
  onCreateTimeSlot: (day: DayOfWeek) => void;
  canManage: boolean;
  filteredView?: boolean;
  isSelectionMode?: boolean;
  selectedTimeSlots?: Set<string>;
  onTimeSlotSelection?: (timeSlotId: string, isSelected: boolean) => void;
}

const DAYS: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const TimeSlotGrid: React.FC<TimeSlotGridProps> = ({
  timeSlots,
  onEditTimeSlot,
  onDeleteTimeSlot,
  onCreateTimeSlot,
  canManage,
  filteredView = false,
  isSelectionMode = false,
  selectedTimeSlots = new Set(),
  onTimeSlotSelection,
}) => {
  const { t } = useTranslation();
  const { workoutTypes, loading: workoutTypesLoading } = useWorkoutTypes();

  const getTimeSlotsForDay = (day: DayOfWeek) => {
    return timeSlots
      .filter((slot) => slot.day === day && slot.isActive)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const getWorkoutTypeName = (workoutTypeId: string) => {
    if (workoutTypesLoading) return t('common.loading');
    const workoutType = workoutTypes.find((wt) => wt.id === workoutTypeId);
    return workoutType?.name || 'Unknown';
  };

  const getWorkoutTypeColor = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find((wt) => wt.id === workoutTypeId);
    return workoutType?.color || '#3498db';
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    const date = new Date();
    date.setHours(hour, minute);

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getDayTranslation = (day: DayOfWeek) => {
    return t(`schedule.days.${day}`);
  };

  const handleSlotSelection = (slotId: string, checked: boolean) => {
    if (onTimeSlotSelection) {
      onTimeSlotSelection(slotId, checked);
    }
  };

  const renderTimeSlotCard = (slot: TimeSlot) => (
    <div
      key={slot.id}
      className={`p-3 border rounded-lg transition-all ${
        isSelectionMode && selectedTimeSlots.has(slot.id)
          ? 'border-primary bg-primary/5'
          : 'hover:shadow-sm'
      }`}
      style={{
        borderLeftColor: getWorkoutTypeColor(slot.workoutTypeId),
        borderLeftWidth: '4px',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isSelectionMode && (
            <Checkbox
              checked={selectedTimeSlots.has(slot.id)}
              onCheckedChange={(checked) => handleSlotSelection(slot.id, !!checked)}
              className="mr-2"
            />
          )}
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">
            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
          </span>
        </div>
        {canManage && !isSelectionMode && (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEditTimeSlot(slot)}
              className="h-6 w-6 p-0"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDeleteTimeSlot(slot)}
              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Badge
          variant="secondary"
          className="text-xs"
          style={{
            backgroundColor: `${getWorkoutTypeColor(slot.workoutTypeId)}20`,
          }}
        >
          {getWorkoutTypeName(slot.workoutTypeId)}
        </Badge>

        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3 w-3" />
          <span>
            {t('schedule.maxClients')}: {slot.maxClients}
          </span>
        </div>
      </div>
    </div>
  );

  // If this is a filtered view, show all matching slots in a single list
  if (filteredView && timeSlots.length > 0) {
    const daysWithSlots = DAYS.filter(day => getTimeSlotsForDay(day).length > 0);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {daysWithSlots.map((day) => {
          const daySlots = getTimeSlotsForDay(day);

          return (
            <Card key={day} className="h-fit">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    {getDayTranslation(day)}
                  </CardTitle>
                  {canManage && !isSelectionMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onCreateTimeSlot(day)}
                      className="h-8 px-2"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {daySlots.map(renderTimeSlotCard)}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Default view - show all days
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
      {DAYS.map((day) => {
        const daySlots = getTimeSlotsForDay(day);

        return (
          <Card key={day} className="h-fit">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">
                  {getDayTranslation(day)}
                </CardTitle>
                {canManage && !isSelectionMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onCreateTimeSlot(day)}
                    className="h-8 px-2"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {daySlots.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 text-sm">
                  {t('schedule.noTimeSlots')}
                </div>
              ) : (
                daySlots.map(renderTimeSlotCard)
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default TimeSlotGrid;
